
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Truck, ClipboardCheck, Clock, AlertTriangle, Sparkles, Info, 
  Radio, CheckCircle2, Check, Cloud,
  Calendar, PackageSearch, Bell
} from 'lucide-react';
import { OrderStatus, Order, Product, SalesGoal, AppNotification } from './types';
import { Card, StatCard, Badge, CircularGoalGauge } from './SharedComponents';
import { GoogleGenAI } from "@google/genai";
import { STATUS_COLORS } from './constants';

const OPENWEATHER_KEY = "5e1e4ff1831f6a0d03e117c57f9227e2"; 
const CITY_NAME = "Sorocaba,BR";

export const DashboardPage = ({ orders = [], products = [], notifications = [], onSelectOrder, goals = [] }: any) => {
  const today = new Date().toISOString().split('T')[0];
  const [aiBriefing, setAiBriefing] = useState<string | null>(null);
  const [weatherData, setWeatherData] = useState<any>(null);

  const { currentMonthCapitalized, currentYear } = useMemo(() => {
    const now = new Date();
    const monthName = now.toLocaleString('pt-BR', { month: 'long' });
    return {
      currentMonthCapitalized: monthName.charAt(0).toUpperCase() + monthName.slice(1),
      currentYear: now.getFullYear()
    };
  }, []);

  const activeGoal = useMemo(() => {
    const monthGoal = goals.find((g: SalesGoal) => g.month === currentMonthCapitalized && g.year === currentYear);
    if (monthGoal) return monthGoal;
    return goals.find((g: SalesGoal) => g.status === 'ACTIVE') || { targetValue: 10000, month: 'Referência', year: 2025 };
  }, [goals, currentMonthCapitalized, currentYear]);

  const metaTarget = activeGoal.targetValue;
  const currentTotalRevenue = useMemo(() => orders.reduce((acc: number, o: Order) => acc + (o.estimatedCost || 0), 0), [orders]);
  const metaPercentage = Math.min(Math.round((currentTotalRevenue / metaTarget) * 100), 100);

  const upcomingOrders = useMemo(() => {
    return orders
      .filter((o: any) => o.dataEvento >= today && o.status !== OrderStatus.CONCLUIDO)
      .sort((a: any, b: any) => a.dataEvento.localeCompare(b.dataEvento))
      .slice(0, 5);
  }, [orders, today]);

  const fetchWeather = useCallback(async () => {
    try {
      const response = await fetch(`https://api.openweathermap.org/data/2.5/weather?q=${CITY_NAME}&units=metric&lang=pt_br&appid=${OPENWEATHER_KEY}`);
      const data = await response.json();
      if (data.cod === 200) setWeatherData(data);
    } catch (error) { console.error("Erro clima", error); }
  }, []);

  const ruptureForecast = useMemo(() => {
    const now = new Date();
    const next7Days: string[] = [];
    for(let i=0; i<7; i++) {
       const d = new Date(now);
       d.setDate(d.getDate() + i);
       next7Days.push(d.toISOString().split('T')[0]);
    }
    const itemShortages: any[] = [];
    products.forEach((p: Product) => {
       next7Days.forEach(date => {
          const demandOnDate = orders
            .filter((o: Order) => o.dataEvento <= date && o.diaRetirada >= date && o.status !== OrderStatus.CONCLUIDO)
            .reduce((acc: number, o: Order) => {
               const itemInOrder = o.materials?.find((m: any) => m.name === p.name);
               return acc + (itemInOrder?.qty || 0);
            }, 0);
          if (demandOnDate > p.quantity) {
             const existing = itemShortages.find(s => s.productName === p.name);
             if (!existing) itemShortages.push({ productName: p.name, shortage: demandOnDate - p.quantity, date });
          }
       });
    });
    return itemShortages;
  }, [orders, products]);

  const generateAiInsights = useCallback(async () => {
    if (!weatherData) return;
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY || '' });
      const inventoryStatus = ruptureForecast.length > 0 
        ? `Alerta de ruptura em ${ruptureForecast.length} itens.` 
        : "Estoque equilibrado para os próximos 7 dias.";
      
      const prompt = `
        Atue como um Gestor de Logística Sênior para uma empresa de eventos em Sorocaba/SP.
        Contexto Atual:
        - Clima: ${weatherData.weather[0].description}, Temperatura: ${Math.round(weatherData.main.temp)}°C.
        - Vento: ${Math.round(weatherData.wind.speed * 3.6)}km/h.
        - Inventário: ${inventoryStatus}
        - Próximos Eventos: ${upcomingOrders.length} eventos críticos agendados.
        
        Com base nisso, forneça um conselho operacional estratégico de no máximo 20 palavras para a equipe hoje. 
        Foque em segurança (devido ao vento/clima) ou eficiência (devido ao estoque/pedidos).
        Seja direto, profissional e motivador.
      `;
      
      const response = await ai.models.generateContent({ 
        model: 'gemini-3-flash-preview', 
        contents: prompt 
      });
      setAiBriefing(response.text || "Mantenha o foco na segurança e eficiência operacional.");
    } catch (_e) { 
      setAiBriefing("Atenção redobrada com a segurança das montagens e conferência de carga."); 
    }
  }, [weatherData, ruptureForecast.length, upcomingOrders.length]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchWeather();
    }, 0);
    return () => clearTimeout(timer);
  }, [fetchWeather]);

  useEffect(() => { 
    if (weatherData) {
      const timer = setTimeout(() => {
        generateAiInsights();
      }, 0);
      return () => clearTimeout(timer);
    } 
  }, [weatherData, generateAiInsights]);

  return (
    <div className="space-y-8 animate-in fade-in duration-700 pb-20 p-4 md:p-8 lg:p-10">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-black text-secondary uppercase tracking-tighter leading-none italic">Painel de Controle</h1>
          <p className="text-gray-400 text-[9px] md:text-[10px] font-bold uppercase tracking-widest italic mt-2">Inteligência Operacional Ativa</p>
        </div>
      </div>

      {/* Row 1: Goal and Upcoming */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
        <div className="lg:col-span-4 xl:col-span-3">
          <CircularGoalGauge 
            label={`META ${activeGoal.month.toUpperCase()}`} 
            percentage={metaPercentage} 
            targetValue={metaTarget} 
            currentValue={currentTotalRevenue} 
          />
        </div>

        <div className="lg:col-span-8 xl:col-span-9">
          <Card className="p-6 md:p-8 bg-white border-2 border-gray-100 flex flex-col h-full shadow-sm">
             <div className="flex justify-between items-center mb-6 md:mb-8">
                <div>
                   <h3 className="text-[10px] md:text-[11px] font-black text-primary uppercase tracking-widest leading-none">PRÓXIMOS EVENTOS</h3>
                   <p className="text-[7px] md:text-[8px] text-gray-400 font-bold uppercase tracking-widest mt-1.5 opacity-60">Operações críticas nos próximos 14 dias</p>
                </div>
                <div className="p-2 bg-gray-50 rounded-lg text-gray-400 hidden sm:block"><Calendar size={14} /></div>
             </div>
             <div className="space-y-3 flex-1">
                {upcomingOrders.length > 0 ? upcomingOrders.map((o: any) => (
                  <div key={o.id} onClick={() => onSelectOrder(o)} className="p-4 md:p-5 bg-gray-50/50 rounded-[28px] border border-gray-100 flex items-center justify-between group hover:border-primary/20 hover:bg-white hover:shadow-lg transition-all cursor-pointer">
                     <div className="flex items-center gap-3 md:gap-4 min-w-0">
                        <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-xl md:rounded-2xl flex items-center justify-center text-primary shadow-sm group-hover:bg-primary group-hover:text-white transition-all shrink-0"><Truck size={20} className="md:w-6 md:h-6" /></div>
                        <div className="min-w-0">
                           <p className="font-black text-secondary uppercase text-xs md:text-sm tracking-tight truncate">{o.nomeEvento}</p>
                           <p className="text-[8px] md:text-[10px] text-gray-400 font-bold uppercase tracking-widest flex items-center gap-1.5 mt-0.5"><Clock size={10} className="text-primary" /> {o.horarioEntrega} • {o.dataEvento}</p>
                        </div>
                     </div>
                     <Badge className={`${STATUS_COLORS[o.status as OrderStatus] || 'bg-gray-100 text-gray-400'} text-[7px] md:text-[8px] border-none px-2.5 md:px-3 ml-2 shrink-0`}>{o.status}</Badge>
                  </div>
                )) : (
                  <div className="flex flex-col items-center justify-center h-full opacity-20 py-10">
                     <Calendar size={40} strokeWidth={1} />
                     <p className="text-[10px] font-black uppercase mt-4">Sem eventos programados</p>
                  </div>
                )}
             </div>
          </Card>
        </div>
      </div>

      {/* Row 2: Basic Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="Agendas Hoje" value={orders.filter((o:any) => o.dataEvento === today).length} icon={ClipboardCheck} colorClass="bg-primary/10 text-primary" />
        <StatCard title="Clima Local" value={weatherData ? `${Math.round(weatherData.main.temp)}°C` : '...'} icon={Cloud} colorClass="bg-blue-50 text-blue-500" />
        <StatCard title="Alerta Ruptura" value={ruptureForecast.length} icon={AlertTriangle} colorClass="bg-red-50 text-red-600" />
        <StatCard title="Saldo em Campo" value={orders.filter((o:any) => o.status !== OrderStatus.CONCLUIDO).length} icon={PackageSearch} colorClass="bg-amber-50 text-amber-600" />
      </div>

      {/* Row 3: AI Insights, Recent Alerts & Inventory Health */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
         <div className="lg:col-span-5 space-y-6">
            <Card className="p-6 md:p-8 bg-secondary text-white border-none shadow-2xl relative overflow-hidden group min-h-[220px] flex flex-col justify-center rounded-[40px]">
               <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:scale-110 transition-transform duration-1000 pointer-events-none"><Sparkles size={120} /></div>
               <div className="relative z-10 space-y-6">
                  <div className="flex items-center gap-3">
                     <div className="p-2 bg-primary/20 rounded-xl text-primary"><Radio size={20} className="animate-pulse" /></div>
                     <h3 className="text-base md:text-lg font-black uppercase tracking-tight">IA Briefing Climático</h3>
                  </div>
                  <div className="p-5 bg-white/5 rounded-[28px] border border-white/10 italic">
                     <p className="text-[7px] font-black text-primary uppercase tracking-[0.2em] mb-2">Parecer Técnico Gemini:</p>
                     <p className="text-xs md:text-sm font-bold text-white/80 leading-relaxed italic">
                        {aiBriefing || "Verificando condições..."}
                     </p>
                  </div>
               </div>
            </Card>

            <Card className="p-6 md:p-8 bg-white border-2 border-gray-100 shadow-xl rounded-[40px] flex flex-col min-h-[300px]">
               <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center gap-3">
                     <div className="p-2 bg-primary/10 text-primary rounded-xl"><Bell size={20} /></div>
                     <h3 className="text-base font-black uppercase text-secondary tracking-tight">Atividade Recente</h3>
                  </div>
               </div>
               <div className="space-y-3 flex-1 overflow-y-auto pr-2 scrollbar-hide">
                  {notifications.slice(0, 5).map((n: AppNotification) => (
                    <div key={n.id} className={`p-4 rounded-[22px] border ${n.type === 'error' ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'} transition-all hover:bg-white hover:shadow-lg`}>
                       <div className="flex justify-between items-start mb-1">
                          <p className={`text-[9px] font-black uppercase tracking-widest ${n.type === 'error' ? 'text-red-600' : 'text-primary'}`}>{n.title}</p>
                          <span className="text-[8px] font-bold text-gray-400">{n.timestamp}</span>
                       </div>
                       <p className="text-[11px] font-bold text-slate-600 leading-tight">{n.message}</p>
                    </div>
                  ))}
                  {notifications.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-full opacity-20">
                       <Info size={32} />
                       <p className="text-[9px] font-black uppercase mt-2 tracking-widest">Sem logs recentes</p>
                    </div>
                  )}
               </div>
            </Card>
         </div>

         <div className="lg:col-span-7">
            <Card className={`p-6 md:p-8 border-2 transition-all relative overflow-hidden flex flex-col min-h-[535px] rounded-[48px] ${ruptureForecast.length > 0 ? 'bg-red-50 border-red-100' : 'bg-white border-gray-100'}`}>
               <div className="relative z-10 flex flex-col h-full">
                  <div className="flex justify-between items-start mb-8">
                     <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-xl ${ruptureForecast.length > 0 ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}>
                           {ruptureForecast.length > 0 ? <AlertTriangle size={20} /> : <Check size={20} />}
                        </div>
                        <div>
                           <h3 className={`text-base md:text-lg font-black uppercase tracking-tight ${ruptureForecast.length > 0 ? 'text-red-900' : 'text-secondary'}`}>Saúde do Inventário (7d)</h3>
                           <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-1">Sincronização live com agendas</p>
                        </div>
                     </div>
                  </div>
                  <div className="space-y-4 flex-1">
                     {ruptureForecast.length > 0 ? (
                        ruptureForecast.map((s, idx) => (
                           <div key={idx} className="bg-white p-5 rounded-[32px] border border-red-200 flex justify-between items-center shadow-sm animate-in slide-in-from-bottom-2">
                              <div className="min-w-0">
                                 <p className="text-[11px] font-black text-secondary uppercase truncate pr-4">{s.productName}</p>
                                 <p className="text-[9px] text-red-500 font-bold uppercase tracking-widest mt-1.5 flex items-center gap-2">
                                    <Clock size={12} /> Déficit: {s.shortage} un em {s.date}
                                 </p>
                              </div>
                              <Badge className="bg-red-500 text-white border-none text-[8px] font-black px-4 py-2 shadow-lg shadow-red-100">SUB-LOCAR</Badge>
                           </div>
                        ))
                     ) : (
                        <div className="flex flex-col items-center justify-center h-full gap-6 opacity-30">
                           <CheckCircle2 size={64} className="text-green-600" />
                           <div className="text-center">
                              <p className="text-sm font-black uppercase tracking-[0.4em] text-secondary">Estoque Equilibrado</p>
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2">Nenhuma ruptura prevista para os próximos 7 dias</p>
                           </div>
                        </div>
                     )}
                  </div>
               </div>
               {/* Gráfico Visual do Estoque */}
               <div className="mt-8 pt-8 border-t border-gray-50 flex items-end justify-between h-20 px-4 opacity-40">
                  {[40, 70, 45, 90, 65, 80, 50, 60, 40, 85].map((h, i) => (
                    <div key={i} className="w-4 bg-primary/20 rounded-t-lg transition-all" style={{ height: `${h}%` }}></div>
                  ))}
               </div>
            </Card>
         </div>
      </div>
    </div>
  );
};
